import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Alert, Table, Badge, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import TenantManagementLayout from '../tenants/TenantManagementLayout';

const ModelManagement = ({ auth }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({
    provider: 'openai',
    model_id: '',
    model_name: '',
    model_type: 'text',
    input_cost_per_million: '',
    output_cost_per_million: '',
    context_window: '',
    cost_per_million_chars: '',
    cost_per_minute: '',
    cost_per_image: '',
    image_quality: '',
    cost_per_second: '',
    is_active: true,
    is_recommended: false,
    supports_temperature: true,
    default_voice: '',
    notes: ''
  });

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/models', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setModels(data.models);
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddModel = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await fetch('/api/admin/models', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanFormData(formData))
      });

      if (response.ok) {
        setShowAddModal(false);
        resetForm();
        setSuccess('Model added successfully');
        fetchModels();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditModel = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await fetch(`/api/admin/models/${editingModel.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanFormData(formData))
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingModel(null);
        resetForm();
        setSuccess('Model updated successfully');
        fetchModels();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      try {
        const response = await fetch(`/api/admin/models/${modelId}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          setSuccess('Model deleted successfully');
          fetchModels();
          setTimeout(() => setSuccess(null), 3000);
        } else {
          const errorData = await response.json();
          setError(errorData.error);
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleToggleActive = async (model) => {
    try {
      const response = await fetch(`/api/admin/models/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...model, is_active: !model.is_active })
      });

      if (response.ok) {
        fetchModels();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleRecommended = async (model) => {
    try {
      const response = await fetch(`/api/admin/models/${model.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...model, is_recommended: !model.is_recommended })
      });

      if (response.ok) {
        fetchModels();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const cleanFormData = (data) => {
    const cleaned = { ...data };
    const numericFields = [
      'input_cost_per_million', 'output_cost_per_million', 'context_window',
      'cost_per_million_chars', 'cost_per_minute', 'cost_per_image', 'cost_per_second'
    ];
    numericFields.forEach(field => {
      if (cleaned[field] === '' || cleaned[field] === null) {
        cleaned[field] = null;
      } else {
        cleaned[field] = parseFloat(cleaned[field]);
      }
    });
    return cleaned;
  };

  const resetForm = () => {
    setFormData({
      provider: 'openai',
      model_id: '',
      model_name: '',
      model_type: 'text',
      input_cost_per_million: '',
      output_cost_per_million: '',
      context_window: '',
      cost_per_million_chars: '',
      cost_per_minute: '',
      cost_per_image: '',
      image_quality: '',
      cost_per_second: '',
      is_active: true,
      is_recommended: false,
      supports_temperature: true,
      default_voice: '',
      notes: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (model) => {
    setEditingModel(model);
    setFormData({
      provider: model.provider || 'openai',
      model_id: model.model_id || '',
      model_name: model.model_name || '',
      model_type: model.model_type || 'text',
      input_cost_per_million: model.input_cost_per_million || '',
      output_cost_per_million: model.output_cost_per_million || '',
      context_window: model.context_window || '',
      cost_per_million_chars: model.cost_per_million_chars || '',
      cost_per_minute: model.cost_per_minute || '',
      cost_per_image: model.cost_per_image || '',
      image_quality: model.image_quality || '',
      cost_per_second: model.cost_per_second || '',
      is_active: model.is_active,
      is_recommended: model.is_recommended,
      supports_temperature: model.supports_temperature ?? false,
      default_voice: model.default_voice || '',
      notes: model.notes || ''
    });
    setShowEditModal(true);
  };

  const groupedModels = {
    text: models.filter(m => m.model_type === 'text'),
    tts: models.filter(m => m.model_type === 'tts'),
    image: models.filter(m => m.model_type === 'image'),
    video: models.filter(m => m.model_type === 'video')
  };

  const renderPricing = (model) => {
    switch (model.model_type) {
      case 'text':
        return `$${model.input_cost_per_million || 0}/$${model.output_cost_per_million || 0} per 1M tokens`;
      case 'tts':
        if (model.cost_per_minute) {
          return `$${model.cost_per_minute} per minute`;
        }
        return `$${model.cost_per_million_chars || 0} per 1M chars`;
      case 'image':
        return `$${model.cost_per_image || 0} per image`;
      case 'video':
        return `$${model.cost_per_second || 0} per second`;
      default:
        return '-';
    }
  };

  const renderFormFields = () => {
    switch (formData.model_type) {
      case 'text':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Input Cost per Million Tokens ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={formData.input_cost_per_million}
                onChange={(e) => setFormData({ ...formData, input_cost_per_million: e.target.value })}
                placeholder="e.g., 0.15"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Output Cost per Million Tokens ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={formData.output_cost_per_million}
                onChange={(e) => setFormData({ ...formData, output_cost_per_million: e.target.value })}
                placeholder="e.g., 0.60"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Context Window (tokens)</Form.Label>
              <Form.Control
                type="number"
                value={formData.context_window}
                onChange={(e) => setFormData({ ...formData, context_window: e.target.value })}
                placeholder="e.g., 128000"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Supports Temperature"
                checked={formData.supports_temperature}
                onChange={(e) => setFormData({ ...formData, supports_temperature: e.target.checked })}
              />
            </Form.Group>
          </>
        );
      case 'tts':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Default Voice</Form.Label>
              <Form.Select
                value={formData.default_voice}
                onChange={(e) => setFormData({ ...formData, default_voice: e.target.value })}
              >
                <option value="">Select voice</option>
                <option value="alloy">Alloy</option>
                <option value="echo">Echo</option>
                <option value="fable">Fable</option>
                <option value="onyx">Onyx</option>
                <option value="nova">Nova</option>
                <option value="shimmer">Shimmer</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cost per Million Characters ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={formData.cost_per_million_chars}
                onChange={(e) => setFormData({ ...formData, cost_per_million_chars: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cost per Minute ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={formData.cost_per_minute}
                onChange={(e) => setFormData({ ...formData, cost_per_minute: e.target.value })}
              />
            </Form.Group>
          </>
        );
      case 'image':
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Cost per Image ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.0001"
                value={formData.cost_per_image}
                onChange={(e) => setFormData({ ...formData, cost_per_image: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image Quality</Form.Label>
              <Form.Select
                value={formData.image_quality}
                onChange={(e) => setFormData({ ...formData, image_quality: e.target.value })}
              >
                <option value="">Select quality</option>
                <option value="standard">Standard</option>
                <option value="hd">HD</option>
              </Form.Select>
            </Form.Group>
          </>
        );
      case 'video':
        return (
          <Form.Group className="mb-3">
            <Form.Label>Cost per Second ($)</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              value={formData.cost_per_second}
              onChange={(e) => setFormData({ ...formData, cost_per_second: e.target.value })}
            />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  const renderModelTable = (modelList, title) => {
    if (modelList.length === 0) return null;

    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{title}</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead>
              <tr>
                <th>Model ID</th>
                <th>Display Name</th>
                <th>Pricing</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {modelList.map(model => (
                <tr key={model.id} style={model.is_recommended ? { backgroundColor: '#e8f5e9' } : {}}>
                  <td>
                    <code>{model.model_id}</code>
                    {model.is_recommended && (
                      <Badge bg="success" className="ms-2">Recommended</Badge>
                    )}
                  </td>
                  <td>{model.model_name}</td>
                  <td>{renderPricing(model)}</td>
                  <td>
                    <Badge bg={model.is_active ? 'success' : 'secondary'}>
                      {model.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    <Button variant="outline-primary" size="sm" onClick={() => openEditModal(model)} className="me-1">
                      Edit
                    </Button>
                    <Button
                      variant={model.is_active ? 'outline-secondary' : 'outline-success'}
                      size="sm"
                      onClick={() => handleToggleActive(model)}
                      className="me-1"
                    >
                      {model.is_active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant={model.is_recommended ? 'warning' : 'outline-warning'}
                      size="sm"
                      onClick={() => handleToggleRecommended(model)}
                      className="me-1"
                    >
                      {model.is_recommended ? '★' : '☆'}
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteModel(model.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <TenantManagementLayout title="AI Models" subtitle="Manage AI models and their pricing">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading models...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  return (
    <TenantManagementLayout title="AI Models" subtitle="Manage AI models and their pricing">
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">AI Models</h4>
          <Button variant="success" onClick={openAddModal}>Add New Model</Button>
        </Card.Header>
        <Card.Body>
          <p className="text-muted">
            Manage AI models and their pricing. Models marked as "Recommended" will be shown as the default option.
          </p>
        </Card.Body>
      </Card>

      {renderModelTable(groupedModels.text, 'Text Models')}
      {renderModelTable(groupedModels.tts, 'Text-to-Speech Models')}
      {renderModelTable(groupedModels.image, 'Image Generation Models')}
      {renderModelTable(groupedModels.video, 'Video Generation Models')}

      {/* Add Model Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New AI Model</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddModel}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Provider</Form.Label>
              <Form.Select
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.model_id}
                onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                placeholder="e.g., gpt-4o-mini"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                placeholder="e.g., GPT-4o Mini"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model Type</Form.Label>
              <Form.Select
                value={formData.model_type}
                onChange={(e) => setFormData({ ...formData, model_type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="tts">Text-to-Speech</option>
                <option value="image">Image Generation</option>
                <option value="video">Video Generation</option>
              </Form.Select>
            </Form.Group>
            {renderFormFields()}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Recommended"
                checked={formData.is_recommended}
                onChange={(e) => setFormData({ ...formData, is_recommended: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Add Model</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Model Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit AI Model</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditModel}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Provider</Form.Label>
              <Form.Select
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.model_id}
                onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model Type</Form.Label>
              <Form.Select
                value={formData.model_type}
                onChange={(e) => setFormData({ ...formData, model_type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="tts">Text-to-Speech</option>
                <option value="image">Image Generation</option>
                <option value="video">Video Generation</option>
              </Form.Select>
            </Form.Group>
            {renderFormFields()}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Recommended"
                checked={formData.is_recommended}
                onChange={(e) => setFormData({ ...formData, is_recommended: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditingModel(null); }}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">Update Model</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(ModelManagement);
